'use client';

import { RadioGroup } from '@radix-ui/react-radio-group';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import { LiveRoundStatus } from '@spikemark/live';
import { CutSetup, DraftLeaderboardCoursesInner, ElasticRoundAnalyticsWithAveragePoints, TournamentPlayersRequestTeamLabelEnum } from '@spikemark/rest-api';
import { ClientTime } from '@spikemark/ui/components/client-time';
import { HelpTooltip } from '@spikemark/ui/components/help-tooltip';
import { MascotWithName } from '@spikemark/ui/components/mascot-with-name';
import { PositionDeltaDisplay } from '@spikemark/ui/components/position-delta-display';
import { ScoreToPar } from '@spikemark/ui/components/score-to-par';
import { TableColumn } from '@spikemark/ui/components/table/types';
import { RadioGroupOption } from '@spikemark/ui/components/ui/radio-group';
import { LeaderboardDisplayMode, LeaderboardType, TieWinner } from '@spikemark/ui/types/leaderboard';
import { cn } from '@spikemark/ui/utils/cn';
import { displayTeamName, formatRankingPoints, formatShortDivisionName, formatThru, strokesToDisplayValue } from '@spikemark/ui/utils/format';
import { isSubstitute } from '@spikemark/ui/utils/player';
import { getLeaderboardRankingDisplay, scoreFormat } from '@spikemark/ui/utils/scoring';
import { CourseLabelDisplay, OverallRankDisplay } from './shared';
type TournamentData = {
  type: LeaderboardType;
  rounds: number;
  todayOnly?: boolean;
  displayMode: LeaderboardDisplayMode;
  sortable?: boolean;
  rankingsEnabled?: boolean;
  divisionName?: string;
  isAdmin?: boolean;
  winnerCount?: number;
  onWinnerSelect?: (winner: TieWinner) => void;
  winner?: TieWinner;
  selectCutLineWinner?: (winner: TieWinner) => void;
  cutLineWinner?: TieWinner;
  allRoundsCompleted?: boolean;
  courseInfoMap: Record<string, DraftLeaderboardCoursesInner>;
  isMultiCourse: boolean;
  playerRankingsDisplayCutoff?: number;
  teamRankingsDisplayCutoff?: number;
  currentRoundId: string;
  roundStatuses?: Record<number, LiveRoundStatus>;
  isUpdatedAfterPublish?: boolean;
  dynamicCutLinesConfig?: {
    rounds: Record<string, number>;
    isLive?: boolean;
  };
  cutLineTiesConfig?: {
    isCutLineTie: boolean;
    cutLineRank?: number;
  };
  cutSetup?: CutSetup | undefined;
};
const renderScore = (rowData: ElasticRoundAnalyticsWithAveragePoints, roundIndex: number, displayMode: LeaderboardDisplayMode) => {
  const strokes = rowData.strokes[roundIndex];
  const score = rowData.scores[roundIndex];
  const teeTime = rowData.rounds?.[roundIndex]?.teeTime;
  if (score > 9000) {
    return 'N/A';
  }
  if (rowData.rounds?.[roundIndex]?.thru === '0' && teeTime) {
    return <ClientTime time={teeTime} />;
  }
  if (displayMode === 'score' && typeof score === 'number') {
    return scoreFormat(score);
  }
  if (typeof strokes === 'number') {
    return strokes;
  }
  return '-';
};
export const createStrokeplayColumns = (options: TournamentData, mobile?: boolean, allRowData?: ElasticRoundAnalyticsWithAveragePoints[], rankings = allRowData ? allRowData.map(({
  currentRank
}) => currentRank) : []): TableColumn<ElasticRoundAnalyticsWithAveragePoints>[] => {
  const currentRoundIdAsNumber = parseInt(options.currentRoundId, 10);
  const scoringColumns: TableColumn<ElasticRoundAnalyticsWithAveragePoints>[] = options.todayOnly ? [{
    header: 'Today',
    cellRenderer: ({
      rowData
    }) => scoreFormat(rowData.scores[rowData.scores.length - 1] ?? 0),
    sortable: options.sortable,
    sortIteratee: rowData => rowData.scores[rowData.scores.length - 1],
    cellClassName: 'numeric',
    hidden: !options.todayOnly,
    width: 52
  }] : Array.apply(null, Array<TableColumn<ElasticRoundAnalyticsWithAveragePoints>>(options.rounds)).map((_, i): TableColumn<ElasticRoundAnalyticsWithAveragePoints> => {
    const isCancelled = options.roundStatuses?.[i + 1] === 'cancelled';
    return {
      header: `RD${i + 1}`,
      cellRenderer: ({
        rowData
      }) => {
        if (isCancelled) {
          return 'CNCL';
        }
        if (options.type === 'player' && isSubstitute(rowData, i + 1)) {
          return <span className="text-gray-500">SUB</span>;
        }
        const roundIsMultiCourse = options.courseInfoMap && Object.values(options.courseInfoMap).filter(x => x.roundId === rowData.roundId).length > 1;
        return <div className={roundIsMultiCourse ? 'flex items-center px-1 lg:px-0' : ''}>
                <div className={roundIsMultiCourse ? 'text-right mr-2 flex-1' : ''}>
                  {renderScore(rowData, i, options.displayMode)}
                </div>
                {roundIsMultiCourse && <CourseLabelDisplay courseInfoMap={options.courseInfoMap} courseLabels={rowData.courseLabels} roundId={i + 1} />}
              </div>;
      },
      cellClassName: cn('numeric border-l border-r', {
        'opacity-50 bg-gray-100': isCancelled
      }),
      sortable: options.sortable,
      sortIteratee: rowData => rowData.scores[i],
      width: mobile ? 50 : 80
    };
  });
  const columns: TableColumn<ElasticRoundAnalyticsWithAveragePoints>[] = [{
    key: 'currentRank',
    header: '#',
    sortable: options.sortable,
    cellClassName: 'numeric min-w-6 lg:min-w-10',
    width: 30,
    cellRenderer: ({
      rowData
    }) => {
      if (!options.cutSetup || isEmpty(options.cutSetup?.teams) && isEmpty(options.cutSetup?.players)) {
        return getLeaderboardRankingDisplay(rankings, rowData.currentRank);
      }
      let isCut = false;
      if (options.type === 'player') {
        const isTeamCut = !options.cutSetup?.teams?.includes(rowData.schoolId);
        const isPlayerAdvanced = rowData.playerId && options.cutSetup?.players?.includes(rowData.playerId);
        isCut = isTeamCut && !isPlayerAdvanced;
      } else {
        isCut = !(options.cutSetup?.teams ?? []).includes(rowData.schoolId);
      }
      if (isCut) {
        return <span className="font-bold text-sm">CUT</span>;
      }
      return rowData.currentRank;
    }
  }, {
    header: 'Winner',
    sortable: options.sortable,
    cellClassName: 'numeric',
    width: 30,
    cellRenderer: ({
      rowData
    }) => {
      const id = (options.type === 'player' ? rowData.playerId : rowData.schoolId)!;
      const isSelected = options.winner?.id === id && (options.type === 'team' ? options.winner.teamLabel === rowData.teamLabel : true);
      return rowData.currentRank === 1 ? <div className="flex justify-center">
            <RadioGroup className="text-clippd-pink mx-auto" onClick={event => {
          event.stopPropagation();
          options.onWinnerSelect?.({
            id,
            teamLabel: rowData.teamLabel
          });
        }}>
              <RadioGroupOption label="" id={'winner'} value={id} checked={isSelected} />
            </RadioGroup>
          </div> : '';
    },
    hidden: !(options.isAdmin && options.allRoundsCompleted)
  }, {
    header: 'Ties',
    sortable: false,
    cellClassName: 'numeric',
    width: 30,
    hidden: !(options.isAdmin && options.allRoundsCompleted),
    cellRenderer: ({
      rowData
    }) => {
      if (options.type === 'player') {
        return '';
      }
      const id = rowData.schoolId!;
      const isSelected = options.cutLineWinner?.id === id && options.cutLineWinner.teamLabel === rowData.teamLabel;
      const cutLineRank = options.cutLineTiesConfig?.cutLineRank;
      const showRadio = rowData.currentRank === cutLineRank;
      return showRadio ? <div className="flex justify-center">
            <RadioGroup className="text-clippd-pink mx-auto" onClick={event => {
          event.stopPropagation();
          options.selectCutLineWinner?.({
            id,
            teamLabel: rowData.teamLabel
          });
        }}>
              <RadioGroupOption label="" id={'cutlineTies'} value={id} checked={isSelected} />
            </RadioGroup>
          </div> : '';
    }
  }, {
    header: '+/-',
    key: 'rankDelta',
    cellRenderer: ({
      rowData
    }) => <PositionDeltaDisplay change={rowData.lastRank === 0 ? 0 : rowData.rankDelta || 0} />,
    sortable: options.sortable,
    cellClassName: 'numeric min-w-6 lg:min-w-10',
    width: 30,
    hidden: options.isAdmin
  }, {
    header: options.type,
    sortable: options.sortable,
    sticky: options.type === 'player',
    key: options.type === 'player' ? 'playerName' : 'schoolName',
    cellClassName: 'lg:w-[40%]',
    cellRenderer: ({
      rowData
    }) => options.type === 'player' ? <MascotWithName alt={rowData.schoolName} path={rowData.userPicture} variant="small" displayMode="player">
            <p className="truncate overflow-hidden font-bold">{rowData.playerName}</p>
            <p className="truncate overflow-hidden">
              {displayTeamName(rowData.schoolName, rowData.teamLabel, rowData.playerId)}
            </p>
            <p className="text-xs lg:hidden">
              <OverallRankDisplay overallRank={rowData.overallPlayerRank} displayCutoff={options.playerRankingsDisplayCutoff} variant="simple" />
            </p>
          </MascotWithName> : <MascotWithName alt={rowData.schoolName} path={rowData.schoolLogo || ''} variant="small" displayMode="team">
            <p className="truncate overflow-hidden">
              {displayTeamName(rowData.schoolName, rowData.teamLabel, rowData.playerId)}
            </p>
            <p className="text-xs lg:hidden">
              <OverallRankDisplay overallRank={rowData.overallTeamRank} displayCutoff={options.teamRankingsDisplayCutoff} variant="simple" />
            </p>
          </MascotWithName>,
    width: 150
  }, {
    header: 'Rank',
    key: 'overallRank',
    cellClassName: 'hidden lg:table-cell',
    hidden: !options.rankingsEnabled || !options.playerRankingsDisplayCutoff && !options.teamRankingsDisplayCutoff,
    cellRenderer: ({
      rowData
    }) => <OverallRankDisplay overallRank={options.type === 'player' ? rowData.overallPlayerRank : rowData.overallTeamRank} displayCutoff={rowData.playerId ? options.playerRankingsDisplayCutoff : options.teamRankingsDisplayCutoff} />,
    width: 40
  }, {
    header: mobile ? '' : 'Total',
    key: 'totalStrokes',
    cellRenderer: ({
      rowData
    }) => options.displayMode === 'score' ? <ScoreToPar score={rowData.totalScore} square /> : strokesToDisplayValue(rowData.totalStrokes),
    sortable: options.sortable,
    cellClassName: 'numeric',
    width: mobile ? 40 : 80
  }, {
    header: 'Thru',
    cellRenderer: ({
      rowData
    }) => {
      const thru = rowData.rounds?.find(x => x.roundId === currentRoundIdAsNumber)?.thru ?? '';
      const [rangeStart, rangeEnd] = String(thru ?? '').split('-').map(x => x === '0' ? '0' : formatThru(x.trim()));
      if (rangeStart && !rangeEnd) {
        return formatThru(rangeStart);
      }
      if (rangeStart === '-') {
        return formatThru(rangeEnd);
      }
      return thru || 'F';
    },
    sortable: options.sortable,
    cellClassName: 'numeric',
    width: mobile ? 50 : 80
  }, ...scoringColumns, {
    header: <HelpTooltip className="uppercase" tooltipContent={`${options.divisionName} points awarded`}>
          Pts
        </HelpTooltip>,
    cellRenderer: ({
      rowData
    }) => {
      const isATeam = options.type === 'team' ? ([TournamentPlayersRequestTeamLabelEnum.A, undefined] as (string | undefined)[]).includes(rowData.teamLabel) : true;
      if (!isATeam) {
        return '-';
      }
      if (options.divisionName && rowData.division && ![options.divisionName, formatShortDivisionName(options.divisionName)].includes(rowData.division)) {
        return '*';
      }
      if (options.isUpdatedAfterPublish) {
        return <span className="blur select-none">000</span>;
      }
      return isNumber(rowData.averagePoints) ? <span className="font-bold">{formatRankingPoints(rowData.averagePoints)}</span> : '-';
    },
    sortable: options.sortable,
    cellClassName: 'numeric',
    hidden: !options.rankingsEnabled || options.isAdmin,
    width: mobile ? 60 : 80
  }];
  return columns;
};